import React, { useCallback, useEffect, useState, useRef } from 'react';
import { View } from '@24i/nxg-sdk-quarks';
import { SeekType } from '@24i/player-base';
import { usePrevious } from '@24i/player-utils-react';
import { useEventsManager } from '@24i/player-ui';
import { usePodcastPlayer } from '@24i/nxg-sdk-smartott/src/context/PodcastPlayerProvider';
export const AudioPlayer = ({ engine, source, autoPlay, startPosition, startPositionType, quality, abrStrategy, onError, onAdBreakStart, onAdBreakEnd, onEnd, onBitrateChange, onPlay, onPause, onSeeking, onSeekingOutOfDVR, onSeeked, children, }) => {
    const playerContainer = useRef(null);
    const [audioPlayer, setAudioPlayer] = useState(null);
    const prevSource = usePrevious(source);
    const prevAudioPlayer = usePrevious(audioPlayer || undefined);
    const prevEngine = usePrevious(engine);
    const destroyRef = useRef();
    const { setPlayerInstanceHandle } = usePodcastPlayer();
    useEventsManager({
        onPlay,
        onPause,
        onSeeking,
        onSeekingOutOfDVR,
        onSeeked,
        onEnd,
        onAdBreakStart,
        onAdBreakEnd,
        onBitrateChange,
    }, audioPlayer);
    const onErrorHandler = useCallback((event) => {
        if (onError)
            onError(event.error);
    }, [onError]);
    const addEventListeners = (playerInstance) => {
        playerInstance.addEventListener('error', onErrorHandler);
    };
    const removeEventListeners = (playerInstance) => {
        playerInstance.removeEventListener('error', onErrorHandler);
    };
    const destroy = useCallback(() => {
        if (!audioPlayer) {
            return;
        }
        audioPlayer.destroy();
        setAudioPlayer(null);
    }, [audioPlayer]);
    destroyRef.current = destroy;
    useEffect(() => () => {
        var _a;
        (_a = destroyRef.current) === null || _a === void 0 ? void 0 : _a.call(destroyRef);
        setPlayerInstanceHandle(null);
    }, []);
    useEffect(() => {
        if (!engine || !playerContainer.current) {
            return;
        }
        if (engine === prevEngine) {
            if (audioPlayer) {
                const didUrlChange = (source === null || source === void 0 ? void 0 : source.url) !== (prevSource === null || prevSource === void 0 ? void 0 : prevSource.url);
                if (source !== prevSource) {
                    try {
                        if (source)
                            audioPlayer.source = source;
                    }
                    catch (error) {
                        if (error instanceof Error)
                            onError === null || onError === void 0 ? void 0 : onError(error);
                        destroy();
                    }
                    if (didUrlChange) {
                        audioPlayer.load().catch((error) => {
                            onError === null || onError === void 0 ? void 0 : onError(error);
                            destroy();
                        });
                    }
                }
                audioPlayer.autoPlay = Boolean(autoPlay);
                audioPlayer.startPosition = startPosition !== null && startPosition !== void 0 ? startPosition : 0;
                audioPlayer.startPositionType = startPositionType !== null && startPositionType !== void 0 ? startPositionType : SeekType.CURRENT_TIME;
                audioPlayer.abrStrategy = abrStrategy;
                audioPlayer.quality = quality;
                return;
            }
        }
        destroy();
        const newVideoPlayer = engine.createPlayer({
            source: source || undefined,
            autoPlay,
            startPosition,
            startPositionType,
            abrStrategy,
            quality,
        }, playerContainer.current);
        if (prevAudioPlayer && prevAudioPlayer !== newVideoPlayer) {
            removeEventListeners(prevAudioPlayer);
        }
        addEventListeners(newVideoPlayer);
        setAudioPlayer(newVideoPlayer);
        setPlayerInstanceHandle(newVideoPlayer);
        newVideoPlayer
            .initializePlayer()
            .then(() => { })
            .catch(() => {
            newVideoPlayer.destroy();
            setAudioPlayer(null);
        });
    }, [engine, source, prevSource]);
    return (React.createElement(React.Fragment, null,
        React.createElement(View, { ref: playerContainer, style: { display: 'none' } }),
        audioPlayer && typeof children === 'function' ? children(audioPlayer) : null));
};
//# sourceMappingURL=index.js.map